import { ClusterInfo, ClusterRow, orderWithClusters } from './rtuCluster';

/**
 * Pure ordering / filtering / paging for the asset lists, shared by the ungrouped list and the grouped tree.
 *
 * Rules (the tables run in `lazy` mode and render exactly what this module returns):
 *  - a child stays directly beneath its master for every sort: roots are sorted by the key, children are sorted by
 *    the same key inside their master;
 *  - a search / column filter that matches only a child keeps the master as context and expanded; a master that
 *    matches keeps all of its children available (collapse still applies unless one of them matches);
 *  - pagination counts root rows (standalone rows and masters). A page holds `pageSize` roots plus every visible
 *    child of those roots, so a page boundary can never fall between a master and its children. The paginator's
 *    total is the number of roots.
 */

export interface SortMeta {
  field: string;
  order: number | null | undefined;
}

export interface ColumnFilter {
  value?: unknown;
  matchMode?: string;
}

export type ColumnFilters = Record<string, ColumnFilter | null | undefined>;

export interface ClusterRowInfo extends ClusterInfo {
  /** Master only: children are listed (not collapsed, or a child matches the active filter). */
  expanded: boolean;
  /** Master shown only because one of its children matches the active filter. */
  context: boolean;
}

export interface ClusterViewOptions<T> {
  /** Active filter predicate; absent or null means no filter. */
  matches?: ((row: T) => boolean) | null;
  /** Sort comparator applied to roots and, separately, to the children of each master. */
  compare?: ((a: T, b: T) => number) | null;
  collapsed?: ReadonlySet<number>;
  /** Paging by root rows; both must be given to page. */
  first?: number;
  pageSize?: number;
}

export interface ClusterViewResult<T> {
  /** Rows of the requested page (all rows when not paging), masters followed by their visible children. */
  rows: T[];
  /** Same ordering for every page (used by select-all). */
  allRows: T[];
  info: Record<number, ClusterRowInfo>;
  /** Number of root rows after filtering. */
  rootCount: number;
  /** First root index actually used (clamped to the last page). */
  first: number;
}

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

/** Null/blank sort last in both directions; numbers numerically; everything else as case-insensitive natural text. */
export const compareValues = (a: unknown, b: unknown): number => {
  const aBlank = isBlank(a);
  const bBlank = isBlank(b);
  if (aBlank || bBlank) {
    return aBlank === bBlank ? 0 : aBlank ? 1 : -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

export const makeRowComparator = <T>(
  sortMeta: SortMeta[] | null | undefined,
  valueOf: (row: T, field: string) => unknown
): ((a: T, b: T) => number) | null => {
  const active = (sortMeta ?? []).filter((meta) => meta && meta.field && meta.order);
  if (active.length === 0) {
    return null;
  }
  return (a, b) => {
    for (const meta of active) {
      const left = valueOf(a, meta.field);
      const right = valueOf(b, meta.field);
      const direction = (meta.order as number) < 0 ? -1 : 1;
      // blanks stay last whichever the direction
      const result = isBlank(left) || isBlank(right) ? compareValues(left, right) : compareValues(left, right) * direction;
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };
};

const normalize = (value: unknown) => String(value ?? '').toLowerCase();

/** The two match modes the asset lists use. Anything else behaves like "contains". */
export const matchesColumnFilters = <T>(
  row: T,
  filters: ColumnFilters | null | undefined,
  valueOf: (row: T, field: string) => unknown
): boolean =>
  Object.entries(filters ?? {}).every(([field, filter]) => {
    if (field === 'global' || !filter || isBlank(filter.value)) {
      return true;
    }
    const needle = normalize(filter.value);
    const value = normalize(valueOf(row, field));
    return filter.matchMode === 'equals' ? value === needle : value.includes(needle);
  });

export const hasActiveColumnFilter = (filters: ColumnFilters | null | undefined): boolean =>
  Object.entries(filters ?? {}).some(([field, filter]) => field !== 'global' && !!filter && !isBlank(filter.value));

interface Group<T> {
  root: T;
  children: T[];
}

const stableSort = <T>(items: T[], compare: (a: T, b: T) => number): T[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => compare(left.item, right.item) || left.index - right.index)
    .map((entry) => entry.item);

export const buildClusterView = <T extends ClusterRow>(rows: T[], options: ClusterViewOptions<T> = {}): ClusterViewResult<T> => {
  const { matches, compare, collapsed } = options;

  // Structure is derived from every displayed row so a master is known even when it does not match the filter.
  const structure = orderWithClusters(rows);
  let groups: Group<T>[] = [];
  structure.rows.forEach((row) => {
    if (structure.info[row.id].depth === 0) {
      groups.push({ root: row, children: [] });
    } else {
      groups[groups.length - 1].children.push(row);
    }
  });

  const context = new Set<number>();
  const forcedOpen = new Set<number>();
  if (matches) {
    groups = groups.flatMap((group) => {
      const rootMatches = matches(group.root);
      const matchingChildren = group.children.filter(matches);
      if (!rootMatches && matchingChildren.length === 0) {
        return [];
      }
      if (!rootMatches) {
        context.add(group.root.id);
      }
      if (matchingChildren.length > 0) {
        forcedOpen.add(group.root.id);
      }
      return [{ root: group.root, children: rootMatches ? group.children : matchingChildren }];
    });
  }

  if (compare) {
    groups = stableSort(groups, (a, b) => compare(a.root, b.root)).map((group) => ({
      root: group.root,
      children: stableSort(group.children, compare),
    }));
  }

  const info: Record<number, ClusterRowInfo> = {};
  const flatten = (list: Group<T>[]): T[] =>
    list.flatMap((group) => {
      const expanded = group.children.length > 0 && (forcedOpen.has(group.root.id) || !collapsed?.has(group.root.id));
      info[group.root.id] = {
        depth: 0,
        childCount: group.children.length,
        masterId: null,
        isLastChild: false,
        expanded: group.children.length > 0 ? expanded : false,
        context: context.has(group.root.id),
      };
      if (!expanded) {
        return [group.root];
      }
      return [
        group.root,
        ...group.children.map((child, index) => {
          info[child.id] = {
            depth: 1,
            childCount: 0,
            masterId: group.root.id,
            isLastChild: index === group.children.length - 1,
            expanded: false,
            context: false,
          };
          return child;
        }),
      ];
    });

  const allRows = flatten(groups);
  const rootCount = groups.length;
  const paging = options.pageSize !== undefined && options.pageSize > 0 && options.first !== undefined;
  if (!paging) {
    return { rows: allRows, allRows, info, rootCount, first: 0 };
  }
  const pageSize = options.pageSize as number;
  const lastPageStart = rootCount > 0 ? Math.floor((rootCount - 1) / pageSize) * pageSize : 0;
  const first = Math.max(0, Math.min(options.first as number, lastPageStart));
  // `flatten` already filled `info` for every group; the page is a contiguous slice of whole groups.
  const pageRows = flatten(groups.slice(first, first + pageSize));
  return { rows: pageRows, allRows, info, rootCount, first };
};
